import { useState } from 'react';
import { ChevronRight, ChevronDown } from 'lucide-react';
import { useI18n } from '@/lib/i18n';
import { formatSignedUsd } from '@/lib/money-format';
import {
  entryLabelKey, groupLabelKey, formatDuration, formatLedgerDate, ledgerRangeParts, engineName, sumEntries,
  groupDurationSeconds, isExpandable, collapseEntries, receiptHref, groupReceiptEntryId,
  sessionMode, isTextOnlySession, streamShares, barWidthPercent,
  type LedgerGroup,
} from './ledger-labels';

export function LedgerGroupRow({ group, longestSeconds = 0 }: {
  group: LedgerGroup;
  /** The longest session on screen — the duration bar's scale. */
  longestSeconds?: number;
}) {
  const { t, locale } = useI18n();
  const [open, setOpen] = useState(false);
  const expandable = isExpandable(group);
  const total = sumEntries(group.entries);
  // Rows, not entries: a reconnecting synthesis stream bills once per reconnect
  // and would otherwise fill this list with dozens of identical lines.
  const rows = collapseEntries(group.entries);
  const durationSeconds = groupDurationSeconds(rows);
  const duration = formatDuration(durationSeconds);
  // The row's number stays the LONGEST stream — how long the session was. The
  // streams are concurrent, so summing them would state a five-minute call as
  // ten, and the header above this list adds these same numbers up.
  const mode = sessionMode(group);
  const textOnly = mode !== null && isTextOnlySession(group);
  const shares = streamShares(group);
  const sharesTotal = shares.reduce((total, share) => total + share.seconds, 0) || 1;
  // At most one entry per group carries a receipt today (one mint per payment
  // intent), and a top-up group is always a single entry, so this looks at the
  // GROUP, not the expanded rows — the expanded list is `DisplayEntry`, which
  // has no id and never renders for a top-up anyway (isExpandable is false for
  // a one-entry group).
  const receiptEntryId = groupReceiptEntryId(group);
  // Every session on this page is a KizunaAI one, so the row credits only the
  // engine, as the app's provider picker does ("Powered by Qwen").
  const engine = engineName(group.engine, group.region, t);
  const range = group.startedAt !== undefined
    ? ledgerRangeParts(group.startedAt, group.endedAt ?? group.occurredAt, locale)
    : null;

  return (
    <div className="wallet-card__ledger-group">
      <div className="wallet-card__ledger-row">
        {expandable ? (
          <button
            type="button"
            className="wallet-card__ledger-toggle"
            aria-expanded={open}
            aria-label={t(open
              ? 'dashboard.wallet.ledger.collapseRow'
              : 'dashboard.wallet.ledger.expandRow')}
            onClick={() => setOpen((v) => !v)}
          >
            {open ? <ChevronDown size={16} /> : <ChevronRight size={16} />}
          </button>
        ) : (
          // A single-entry group has nothing to disclose; the spacer keeps the
          // date column aligned with the expandable rows above and below it.
          <span className="wallet-card__ledger-toggle-spacer" />
        )}
        <span className="wallet-card__ledger-date">
          {range
            ? <>
                <span className="wallet-card__ledger-date-part">{range.start}</span>
                {range.end !== null && <>{range.separator}<span className="wallet-card__ledger-date-part">{range.end}</span></>}
              </>
            : formatLedgerDate(group.occurredAt, locale)}
        </span>
        {/* The mode this session ran in, in the app's words. It replaces a
            label that read "Translation session" on every row alike — a column
            that asked a question and then answered it identically each time.
            A group whose entries do not name a mode keeps the generic label. */}
        <span className="wallet-card__ledger-label">
          {mode
            ? <>
                {t(`dashboard.wallet.ledger.mode.${mode}`)}
                {textOnly && (
                  <span className="wallet-card__ledger-qualifier">
                    {' · '}{t('dashboard.wallet.ledger.textOnly')}
                  </span>
                )}
              </>
            : t(groupLabelKey(group.kind))}
          {engine && (
            <span className="wallet-card__ledger-engine">
              {t('dashboard.wallet.ledger.poweredBy').replace('{name}', engine)}
            </span>
          )}
        </span>
        {/* Length is the session; the split is who spoke. Two encodings on one
            object, which is what lets the row carry both streams without a
            second column of digits. The exact duration follows it. */}
        <span className="wallet-card__ledger-streams">
          {shares.length > 0 && (
            <span
              className="wallet-card__stream-bar"
              style={{ width: `${barWidthPercent(durationSeconds ?? 0, longestSeconds)}%` }}
            >
              {shares.map((share) => (
                <i
                  key={share.kind}
                  className={`wallet-card__stream wallet-card__stream--${share.kind}`}
                  style={{ width: `${(share.seconds / sharesTotal) * 100}%` }}
                />
              ))}
            </span>
          )}
        </span>
        {/* Always one grid item here, receipt or not: `wallet-card__ledger-row`
            is a fixed-column grid, and a row that only sometimes contributes a
            6th item would push its duration/amount into different columns than
            every row beside it. An empty span collapses to zero width. */}
        <span className="wallet-card__ledger-receipt">
          {receiptEntryId && (
            <a
              className="wallet-card__receipt-link"
              href={receiptHref(receiptEntryId)}
              target="_blank"
              rel="noopener noreferrer"
            >
              {t('dashboard.wallet.ledger.viewReceipt')}
            </a>
          )}
        </span>
        <span className="wallet-card__ledger-duration">{duration}</span>
        <span className={`wallet-card__ledger-amount ${total > 0 ? 'wallet-card__ledger-amount--credit' : ''}`}>
          {total > 0 ? `+${formatSignedUsd(total)}` : formatSignedUsd(total)}
        </span>
      </div>

      {expandable && open && (
        <ul className="wallet-card__ledger-entries">
          {rows.map((r) => (
            <li key={r.key} className="wallet-card__ledger-entry">
              <span className="wallet-card__ledger-label">
                {t(entryLabelKey(r.kind))}
                {/* `×6` read as "six sentences" to everyone who saw it. It is
                    six STREAM CONNECTIONS: Soniox drops an idle synthesis
                    stream after about 5.3 seconds, so the client reconnects
                    after almost every pause and each reconnect bills its own
                    row. Naming the unit is the whole fix — the number was
                    never wrong, only unlabelled. */}
                {r.count > 1 && (
                  <span className="wallet-card__ledger-count">
                    {' · '}{r.count} {t('dashboard.wallet.ledger.segments')}
                  </span>
                )}
              </span>
              <span className="wallet-card__ledger-duration">{formatDuration(r.billableSeconds)}</span>
              <span className="wallet-card__ledger-amount">{formatSignedUsd(r.amountMicroUsd)}</span>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}
